import React from 'react';
import { Calendar, MapPin, Globe, Ticket, ArrowUpRight, Languages, Video } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const POSTER_URL =
  'https://customer-assets.emergentagent.com/job_09571151-f654-4506-aa32-05da61a50408/artifacts/wtrfz3ph_Illustration%20GTF.jpg';
const EVENT_URL = 'https://ltrk.lv/notikums/seminar-gateway-to-france/';

const content = {
  fr: {
    eyebrow: 'Événement à venir',
    title: 'Gateway to France',
    subtitle: 'Séminaire — Investissements & Export entre la Lettonie et la France',
    organiser:
      'Organisé par la Chambre de Commerce et d\'Industrie de Lettonie (LTRK), en coopération avec l\'Ambassade de France en Lettonie et le Business Club France–Latvia.',
    description:
      "Ce séminaire réunit des représentants institutionnels et économiques des deux pays. Trois temps forts : session investissement, session export, networking. Cadre : Enterprise Europe Network, financé par l'UE (SMP 2025–2028).",
    notice:
      'Présentiel complet — inscriptions ouvertes pour la participation à distance.',
    info: {
      date: '20 mai 2026 — 14:00',
      format: 'Hybride (présentiel / en ligne)',
      language: 'Anglais',
      price: 'Participation gratuite',
      location: 'Locaux de la LTRK, Riga',
    },
    labels: {
      date: 'Date',
      format: 'Format',
      language: 'Langue',
      price: 'Participation',
      location: 'Lieu',
    },
    cta: "Voir l'événement",
    ctaRemote: "S'inscrire (à distance)",
  },
  en: {
    eyebrow: 'Upcoming event',
    title: 'Gateway to France',
    subtitle: 'Seminar — Investment & Export between Latvia and France',
    organiser:
      'Organised by the Latvian Chamber of Commerce and Industry (LCCI), in cooperation with the Embassy of France in Latvia and the Business Club France–Latvia.',
    description:
      'This seminar brings together institutional and business representatives from both countries. Three main parts: investment session, export session, networking. Framework: Enterprise Europe Network, funded by the EU under the SMP Programme (2025–2028).',
    notice:
      'On-site capacity full — remote participation still open for registration.',
    info: {
      date: '20 May 2026 — 14:00',
      format: 'Hybrid (in-person / online)',
      language: 'English',
      price: 'Free of charge',
      location: 'LCCI premises, Riga',
    },
    labels: {
      date: 'Date',
      format: 'Format',
      language: 'Language',
      price: 'Participation',
      location: 'Venue',
    },
    cta: 'View event',
    ctaRemote: 'Register (remote)',
  },
  lv: {
    eyebrow: 'Gaidāmais pasākums',
    title: 'Gateway to France',
    subtitle: 'Seminārs — Investīcijas un eksports starp Latviju un Franciju',
    organiser:
      'Organizē Latvijas Tirdzniecības un rūpniecības kamera (LTRK) sadarbībā ar Francijas vēstniecību Latvijā un Business Club France–Latvia.',
    description:
      'Seminārs pulcē abu valstu institucionālos un biznesa pārstāvjus. Trīs galvenās daļas: investīciju sesija, eksporta sesija, networking. Ietvars: Enterprise Europe Network, finansē ES SMP programma (2025–2028).',
    notice:
      'Klātienes vietas aizpildītas — reģistrācija attālinātai dalībai joprojām atvērta.',
    info: {
      date: '2026. gada 20. maijs — 14:00',
      format: 'Hibrīds (klātienē / tiešsaistē)',
      language: 'Angļu',
      price: 'Bezmaksas',
      location: 'LTRK telpas, Rīga',
    },
    labels: {
      date: 'Datums',
      format: 'Formāts',
      language: 'Valoda',
      price: 'Dalība',
      location: 'Norises vieta',
    },
    cta: 'Skatīt pasākumu',
    ctaRemote: 'Reģistrēties (attālināti)',
  },
};

const UpcomingEventSection = () => {
  const { language } = useLanguage();
  const t = content[language] || content.fr;

  const infoRows = [
    { icon: Calendar, label: t.labels.date, value: t.info.date },
    { icon: Video, label: t.labels.format, value: t.info.format },
    { icon: Languages, label: t.labels.language, value: t.info.language },
    { icon: Ticket, label: t.labels.price, value: t.info.price },
    { icon: MapPin, label: t.labels.location, value: t.info.location },
  ];

  return (
    <section
      id="upcoming-event"
      data-testid="upcoming-event-section"
      className="bg-[#161817] py-20 md:py-28 border-y border-[#3f4816]/40"
    >
      <div className="max-w-[87.5rem] mx-auto px-6 md:px-10">
        {/* Heading */}
        <div className="mb-12 md:mb-14 text-center">
          <div className="inline-flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#d9fb06]" />
            <span className="text-[#d9fb06] text-xs tracking-[0.25em] uppercase font-semibold">
              {t.eyebrow}
            </span>
            <div className="h-px w-10 bg-[#d9fb06]" />
          </div>
          <h2 className="font-black text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95]">
            {t.title}
          </h2>
          <p className="mt-4 text-[#888680] text-base md:text-lg max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Card */}
        <article className="grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-sm overflow-hidden border border-[#3f4816]/60 bg-[#1a1c1b]">
          {/* Poster */}
          <div className="lg:col-span-5 relative bg-[#0f1110]">
            <a
              href={EVENT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="event-poster-link"
              className="block group h-full"
            >
              <div className="aspect-square lg:aspect-auto lg:h-full overflow-hidden">
                <img
                  src={POSTER_URL}
                  alt="Gateway to France — Seminar 20 May 2026"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
            </a>
          </div>

          {/* Details */}
          <div className="lg:col-span-7 p-8 md:p-10 lg:p-12 flex flex-col">
            {/* Notice ribbon */}
            <div className="inline-flex items-center gap-2 self-start mb-6 px-3 py-1.5 rounded-full bg-[#d9fb06]/10 border border-[#d9fb06]/30">
              <span className="w-1.5 h-1.5 rounded-full bg-[#d9fb06] animate-pulse" />
              <span className="text-[#d9fb06] text-[0.7rem] tracking-wider uppercase font-semibold">
                {t.notice}
              </span>
            </div>

            <p className="text-[#c9c7c0] text-sm md:text-base leading-relaxed mb-3 italic">
              {t.organiser}
            </p>
            <p className="text-[#888680] text-sm md:text-base leading-relaxed mb-8">
              {t.description}
            </p>

            {/* Info grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 mb-8 pb-8 border-b border-[#3f4816]/50">
              {infoRows.map((row) => {
                const Icon = row.icon;
                return (
                  <div key={row.label} className="flex items-start gap-3">
                    <div className="mt-0.5 w-8 h-8 rounded-full bg-[#d9fb06]/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={14} className="text-[#d9fb06]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[#888680] text-[0.65rem] tracking-[0.2em] uppercase font-semibold mb-0.5">
                        {row.label}
                      </div>
                      <div className="text-white text-sm md:text-base font-medium leading-tight">
                        {row.value}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 mt-auto">
              <a
                href={EVENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="event-cta-primary"
                className="group inline-flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wider hover:bg-white transition-all duration-300"
              >
                {t.ctaRemote}
                <ArrowUpRight
                  size={18}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </a>
              <a
                href={EVENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="event-cta-secondary"
                className="group inline-flex items-center gap-2 border border-[#d9fb06]/60 text-[#d9fb06] px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wider hover:bg-[#d9fb06]/10 transition-all duration-300"
              >
                <Globe size={16} />
                {t.cta}
              </a>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};

export default UpcomingEventSection;
